import React, { useMemo, useRef, useState } from "react";
import { RiCheckboxCircleFill, RiErrorWarningFill } from "react-icons/ri";
import emailjs from "@emailjs/browser";
import Reveal from "../../components/reveal/Reveal";
import "./donate.css";

const CURRENCIES = {
  BDT: { symbol: "৳", label: "BDT — Bangladeshi taka", presets: [500, 1000, 2500, 5000] },
  USD: { symbol: "$", label: "USD — US dollar", presets: [10, 25, 50, 100] },
  INR: { symbol: "₹", label: "INR — Indian rupee", presets: [500, 1000, 2500, 5000] },
};

const CONTACT_EMAIL = "salesbarishal1988@gmail.com";
const number = new Intl.NumberFormat("en-US");

const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
const isPhone = (value) =>
  /^01[3-9]\d{8}$/.test(value) ||
  /^[6-9]\d{9}$/.test(value) ||
  /^\+?\d{10,15}$/.test(value.replace(/[\s-]/g, ""));

/* One rule per field, so blur and submit can never disagree. */
const RULES = {
  name: (value) => (value.trim() ? null : "Tell us who the donation is from."),
  phone: (value) => {
    if (!value.trim()) return "We need a number to confirm the donation.";
    return isPhone(value) ? null : "That does not look like a valid phone number.";
  },
  email: (value) => {
    if (!value.trim()) return "We send confirmation here.";
    return isEmail(value) ? null : "Check the email address — it is missing something.";
  },
};

const Donate = () => {
  const [currency, setCurrency] = useState("BDT");
  const [preset, setPreset] = useState(CURRENCIES.BDT.presets[1]);
  const [custom, setCustom] = useState("");
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ type: null, message: "" });
  const [loading, setLoading] = useState(false);
  const formRef = useRef(null);

  const config = CURRENCIES[currency];
  const amount = custom ? Number(custom) : preset;
  const formattedAmount = useMemo(
    () => (amount ? number.format(amount) : ""),
    [amount]
  );

  const update = (key) => (event) => {
    setForm((prev) => ({ ...prev, [key]: event.target.value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
    setStatus({ type: null, message: "" });
  };

  /* Validating on blur tells people about a typo while they are still looking
     at the field, rather than after they press Donate. */
  const validateOnBlur = (key) => (event) => {
    const rule = RULES[key];
    if (!rule) return;
    setErrors((prev) => ({ ...prev, [key]: rule(event.target.value) || undefined }));
  };

  const changeCurrency = (event) => {
    const next = event.target.value;
    setCurrency(next);
    setCustom("");
    setPreset(CURRENCIES[next].presets[1]);
  };

  const chooseAmount = (value) => {
    setPreset(value);
    setCustom("");
    setErrors((prev) => ({ ...prev, amount: undefined }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const found = {};
    Object.entries(RULES).forEach(([key, rule]) => {
      const message = rule(form[key]);
      if (message) found[key] = message;
    });
    if (!amount || amount <= 0) found.amount = "Choose an amount or enter your own.";

    setErrors(found);
    if (Object.keys(found).length) {
      setStatus({ type: "error", message: "Check the highlighted fields and try again." });
      return;
    }

    const elements = formRef.current.elements;
    elements.title.value = "Donation Details";
    elements.time.value = new Date().toLocaleString();
    elements.ammount.value = `${amount} ${currency}`;

    setLoading(true);
    setStatus({ type: null, message: "" });
    try {
      await emailjs.sendForm(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_DONATION_TEMPLATE_ID,
        formRef.current,
        { publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY }
      );
      setStatus({
        type: "success",
        message:
          "Thank you. We have your details and will be in touch about completing the donation.",
      });
      setForm({ name: "", phone: "", email: "", message: "" });
      setCustom("");
      setPreset(CURRENCIES[currency].presets[1]);
    } catch {
      setStatus({
        type: "error",
        message: `We could not send that. Email us at ${CONTACT_EMAIL} and we will take it from there.`,
      });
    }
    setLoading(false);
  };

  const fieldProps = (key) => ({
    value: form[key],
    onChange: update(key),
    onBlur: validateOnBlur(key),
    "aria-invalid": Boolean(errors[key]),
    "aria-describedby": errors[key] ? `donate-${key}-error` : undefined,
  });

  return (
    <section
      className="section donate on-dark"
      id="possibility"
      aria-labelledby="donate-title"
    >
      <div className="donate__field" aria-hidden="true" />
      <div className="shell donate__inner">
        <Reveal className="donate__pitch">
          <p className="marker marker--on-dark">
            <span className="marker__num">05</span>
            <span>Give</span>
          </p>
          <h2 id="donate-title" className="t-display-2 donate__title">
            One donation covers{" "}
            <em className="donate__title-accent">one person&apos;s next step.</em>
          </h2>
          <p className="t-lead donate__lead">
            Contributions go towards physiotherapy sessions, medicine, and
            keeping disabled children in school. Tell us what you would like to
            give and we will contact you to complete it.
          </p>

          <dl className="meta donate__terms">
            <div className="meta__row">
              <dt>Goes to</dt>
              <dd>Programme work — physiotherapy, medicine, school support</dd>
            </div>
            <div className="meta__row">
              <dt>Confirmed by</dt>
              <dd>A phone call or an email from us, every time</dd>
            </div>
            <div className="meta__row">
              <dt>Questions</dt>
              <dd>
                <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
              </dd>
            </div>
          </dl>
        </Reveal>

        <Reveal className="donate__card" delay={100}>
          <form ref={formRef} onSubmit={handleSubmit} noValidate>
            <input type="hidden" name="title" />
            <input type="hidden" name="time" />
            <input type="hidden" name="ammount" />

            <fieldset className="donate__amounts">
              <legend className="field__label">Amount</legend>

              <div className="field donate__currency">
                <label className="field__hint" htmlFor="donate-currency">
                  Currency
                </label>
                <select
                  id="donate-currency"
                  name="currency"
                  className="input-sales"
                  value={currency}
                  onChange={changeCurrency}
                >
                  {Object.entries(CURRENCIES).map(([code, meta]) => (
                    <option key={code} value={code}>
                      {meta.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Native radios: arrow keys, screen readers and form state all
                  work without re-implementing the pattern in JavaScript. */}
              <div className="donate__presets">
                {config.presets.map((value) => {
                  const id = `donate-amount-${value}`;
                  return (
                    <div className="donate__preset" key={value}>
                      <input
                        type="radio"
                        id={id}
                        name="presetAmount"
                        className="donate__preset-input"
                        checked={!custom && preset === value}
                        onChange={() => chooseAmount(value)}
                      />
                      <label className="donate__preset-label" htmlFor={id}>
                        <span aria-hidden="true">{config.symbol}</span>
                        {number.format(value)}
                      </label>
                    </div>
                  );
                })}
              </div>

              <div className="field donate__custom">
                <label className="field__hint" htmlFor="donate-custom">
                  Or another amount
                </label>
                <div className="donate__custom-input">
                  <span aria-hidden="true">{config.symbol}</span>
                  <input
                    id="donate-custom"
                    className="input-sales"
                    inputMode="numeric"
                    placeholder="0"
                    value={custom}
                    aria-invalid={Boolean(errors.amount)}
                    aria-describedby={errors.amount ? "donate-amount-error" : undefined}
                    onChange={(event) => {
                      setCustom(event.target.value.replace(/[^0-9]/g, ""));
                      setPreset(null);
                      setErrors((prev) => ({ ...prev, amount: undefined }));
                    }}
                  />
                </div>
                {errors.amount ? (
                  <p className="field__error" id="donate-amount-error">
                    {errors.amount}
                  </p>
                ) : null}
              </div>
            </fieldset>

            <div className="donate__grid">
              <div className="field">
                <label className="field__label" htmlFor="donate-name">
                  Name <span className="field__req">*</span>
                </label>
                <input
                  id="donate-name"
                  name="name"
                  className="input-sales"
                  autoComplete="name"
                  placeholder="Your full name"
                  {...fieldProps("name")}
                />
                {errors.name ? (
                  <p className="field__error" id="donate-name-error">
                    {errors.name}
                  </p>
                ) : null}
              </div>

              <div className="field">
                <label className="field__label" htmlFor="donate-phone">
                  Phone <span className="field__req">*</span>
                </label>
                <input
                  id="donate-phone"
                  name="phone"
                  type="tel"
                  className="input-sales"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="01XXXXXXXXX"
                  {...fieldProps("phone")}
                />
                {errors.phone ? (
                  <p className="field__error" id="donate-phone-error">
                    {errors.phone}
                  </p>
                ) : null}
              </div>

              <div className="field donate__grid-full">
                <label className="field__label" htmlFor="donate-email">
                  Email <span className="field__req">*</span>
                </label>
                <input
                  id="donate-email"
                  name="email"
                  type="email"
                  className="input-sales"
                  autoComplete="email"
                  placeholder="you@example.com"
                  {...fieldProps("email")}
                />
                {errors.email ? (
                  <p className="field__error" id="donate-email-error">
                    {errors.email}
                  </p>
                ) : null}
              </div>

              <div className="field donate__grid-full">
                <label className="field__label" htmlFor="donate-message">
                  Message <span className="field__hint">Optional</span>
                </label>
                <textarea
                  id="donate-message"
                  name="message"
                  className="input-sales"
                  rows={3}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Anything you would like us to know"
                />
              </div>
            </div>

            <div aria-live="polite" className="donate__status">
              {status.type ? (
                <p className={`notice notice--${status.type}`}>
                  {status.type === "success" ? (
                    <RiCheckboxCircleFill size={18} aria-hidden="true" />
                  ) : (
                    <RiErrorWarningFill size={18} aria-hidden="true" />
                  )}
                  {status.message}
                </p>
              ) : null}
            </div>

            <button
              type="submit"
              className="btn-sales btn-sales--donate btn-sales--block donate__submit"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                  Sending…
                </>
              ) : (
                <>
                  Donate {config.symbol}
                  {formattedAmount}
                </>
              )}
            </button>

            <p className="donate__fineprint">
              This form sends us your details. We contact you to arrange the
              transfer — no card details are collected on this page.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default Donate;
